import type { GameSnapshot, PlayerProfile, RoomPublicState, Vector3State } from "../shared/types";

export type PickupKind = "health" | "homing" | "mine";

export interface PickupState {
  id: string;
  kind: PickupKind;
  position: Vector3State;
  radius: number;
  expiresAt: number;
}

export type ClientSnapshot = GameSnapshot & { pickups?: PickupState[] };

export interface SonarContact {
  label: string;
  distance: number;
  kind: "player" | "mine" | "pickup";
}

export function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

export function sanitizeName(value: string): string {
  return value.trim().replace(/\s+/g, " ").slice(0, 18) || "Captain";
}

export function normalizeRoomCode(value: string): string {
  return value.trim().toUpperCase().replace(/[^A-Z0-9-]/g, "").slice(0, 16);
}

export function clampInteger(value: number, min: number, max: number, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.max(min, Math.min(max, Math.floor(value)));
}

export function formatTime(ms: number): string {
  const seconds = Math.ceil(ms / 1000);
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;
  return `${min}:${sec.toString().padStart(2, "0")}`;
}

export function roomJoinBlocked(state: RoomPublicState, savedToken: string | null): boolean {
  if (savedToken) return false;
  if (state.phase !== "lobby") return true;
  return state.players.length >= state.maxPlayers;
}

export function buildSonarContacts(snap: ClientSnapshot, localId: string): SonarContact[] {
  const local = snap.players.find((player) => player.id === localId);
  if (!local) return [];
  const contacts = [
    ...snap.players
      .filter((player) => player.id !== localId && player.connected && player.alive)
      .map((player): SonarContact => ({ kind: "player", label: player.name, distance: distanceMeters(local.position, player.position) })),
    ...snap.mines.map((mine): SonarContact => ({ kind: "mine", label: "Mine", distance: distanceMeters(local.position, mine.position) })),
    ...(snap.pickups ?? []).map((pickup): SonarContact => ({
      kind: "pickup",
      label: pickup.kind === "health" ? "Repair" : pickup.kind === "homing" ? "Homing" : "Mine pack",
      distance: distanceMeters(local.position, pickup.position)
    }))
  ];
  return contacts.sort((a, b) => a.distance - b.distance).slice(0, 5);
}

export function distanceMeters(a: Vector3State, b: Vector3State): number {
  return Math.round(Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z));
}

export function loadStoredProfile(): PlayerProfile | null {
  try {
    const raw = localStorage.getItem("submarine:profile");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed === "object" && parsed !== null && typeof parsed.id === "string" && typeof parsed.displayName === "string") {
      return parsed as PlayerProfile;
    }
    return null;
  } catch {
    return null;
  }
}
